﻿import {
  createFileRoute,
  Outlet,
  redirect,
  Link,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { renovarSementeSimulacao } from "@/lib/modo-valores";
import { instalarGuardaPersistencia } from "@/lib/guarda-persistencia";
import { instalarPonteBancoExterno } from "@/lib/ponte-instalar";
import { precarregarPaginas } from "@/lib/precarregar-paginas";
import { useQuery } from "@tanstack/react-query";
import { nomeExib } from "@/lib/pessoas";
import { Button } from "@/components/ui/button";
import logoUrl from "/milkymoo.webp?url";
import { toast } from "sonner";
import { useNivel } from "@/hooks/useNivel";
import { useAuth } from "@/hooks/useAuth";
import { useMargemLateral } from "@/hooks/usePrefs";
import { useAvisos } from "@/hooks/useAvisos";
import { useNaoLidos } from "@/hooks/useNaoLidos";
import { useMarca } from "@/hooks/useMarca";
import { useParametros } from "@/hooks/useParametros";
import { NotificadorCodigos } from "@/components/NotificadorCodigos";
import { useValores } from "@/hooks/useValores";
import { ouvirJanelaPagina } from "@/lib/janela-pagina";
import { JanelaProvider } from "@/components/JanelaContexto";
import { VoltarInicio } from "@/components/VoltarInicio";
import { janelasAbertas } from "@/components/JanelaCheia";
import { Loader2 } from "lucide-react";

import {
  AlertTriangle,
  Bot,
  LogOut,
  Menu,
  MessageSquare,
  Pin,
  User,
  Building2,
  Home,
  Settings,
  SlidersHorizontal,
  Store,
  Database,
  Archive,
} from "lucide-react";
import {
  useEffect,
  useState,
  useCallback,
  useRef,
  lazy,
  Suspense,
  type ComponentType,
  type LazyExoticComponent,
} from "react";
import { obterClienteDados } from "@/lib/obter-dados";
import { GuardaProntidao } from "@/components/GuardaProntidao";
import { removeSessionItem } from "@/lib/storage-safe";
import { carregarAgendaEventosCache } from "@/lib/agenda-cache";
import { consumirRetornoFisicoDeJanela } from "@/lib/historico-janelas";
import { RodapeCompilacao } from "@/components/RodapeCompilacao";

export const Route = createFileRoute("/_authenticated")({
  ssr: false,
  beforeLoad: async () => {
    const banco = await obterClienteDados();
    // Use helper that safely reads session from the client proxy when available.
    const { obterUsuarioCliente } = await import("@/lib/cliente-dados-helper");
    const usuario = await obterUsuarioCliente();
    if (!usuario) throw redirect({ to: "/auth" });
    const nivel = Number(usuario.user_metadata?.["nivel"] ?? 1);
    if (Number(nivel ?? 9) === 5) {
      if ((banco as any)?.auth && typeof (banco as any).auth.signOut === "function") {
        await (banco as any).auth.signOut({ scope: "local" });
      }
      throw redirect({ to: "/auth" });
    }
    return { user: usuario };
  },
  component: AppLayout,
});

const links = [
  { to: "/inicio", label: "Início", nivelMaximo: 3, icon: Home, cor: "#1565C0" },
  { to: "/assistente-painel", label: "Assistente", nivelMaximo: 3, icon: Bot, cor: "#8E24AA" },
  { to: "/franquia-main", label: "Franquia", nivelMaximo: 4, icon: Store, cor: "#00838F" },
  { to: "/rabelo-main", label: "Rabelo", nivelMaximo: 1, icon: Database, cor: "#00838F" },
  { to: "/atj-main", label: "Arquivo", nivelMaximo: 1, icon: Archive, cor: "#5D4037" },
  { to: "/empresa", label: "Empresa", nivelMaximo: 1, icon: Building2, cor: "#455A64" },
  { to: "/sistema", label: "Sistema", nivelMaximo: 1, icon: Settings, cor: "#374151" },
  {
    to: "/preferencias",
    label: "Preferências",
    nivelMaximo: 1,
    icon: SlidersHorizontal,
    cor: "#C62828",
  },
] as const;

/**
 * Abas do menu horizontal que abrem em janela cheia abaixo do cabeçalho.
 * Cada aba é carregada sob demanda (lazy), de modo que a abertura do
 * aplicativo baixe apenas o login e a janela principal.
 */
const PAGINAS_JANELA: Record<string, LazyExoticComponent<ComponentType> | undefined> = {
  "/inicio": lazy(() => import("@/paginas/inicio").then((m) => ({ default: m.InicioPage }))),
  "/franquia-main": lazy(() =>
    import("@/paginas/pg_franquia/franquia-main").then((m) => ({ default: m.FranquiaMainPage })),
  ),
  "/franquia-pdv-main": lazy(() =>
    import("@/paginas/pg_franquia/franquia-pdv-main").then((m) => ({
      default: m.FranquiaPdvPage,
    })),
  ),
  "/franquia-suprimentos-main": lazy(() =>
    import("@/paginas/pg_franquia/franquia-suprimentos-main").then((m) => ({
      default: m.FranquiaSuprimentosPage,
    })),
  ),
  "/franquia-suprimentos-a-caminho": lazy(() =>
    import("@/paginas/pg_franquia/franquia-suprimentos-a-caminho").then((m) => ({
      default: m.ACaminhoPage,
    })),
  ),
  "/franquia-pdv-matriz": lazy(() =>
    import("@/paginas/pg_franquia/franquia-pdv-matriz").then((m) => ({
      default: m.FranquiaPdvMatrizPage,
    })),
  ),
  "/vendas": lazy(() =>
    import("@/paginas/pg_franquia/franquia-vendas-main").then((m) => ({
      default: m.RegistroVendasPage,
    })),
  ),
  "/franquia-vendas-manuais": lazy(() =>
    import("@/paginas/pg_franquia/franquia-vendas-manuais").then((m) => ({
      default: m.VendasManuaisPage,
    })),
  ),
  "/franquia-vendas-registrar-venda": lazy(() =>
    import("@/paginas/pg_franquia/franquia-vendas-registrar-venda").then((m) => ({
      default: m.RegistrarVendaPage,
    })),
  ),
  "/franquia-vendas-manuais-resumo": lazy(() =>
    import("@/paginas/pg_franquia/franquia-vendas-manuais-resumo").then((m) => ({
      default: m.VendasManuaisResumoPage,
    })),
  ),
  "/franquia-vendas-vendas-dia": lazy(() =>
    import("@/paginas/pg_franquia/franquia-vendas-vendas-dia").then((m) => ({
      default: m.VendasDiaJanela,
    })),
  ),
  "/insumos-consumo": lazy(() =>
    import("@/paginas/pg_insumos/insumos-consumo").then((m) => ({
      default: m.ConsumoMaterialJanela,
    })),
  ),
  "/franquia-vendas-registro-manual": lazy(() =>
    import("@/paginas/pg_franquia/franquia-vendas-registro-manual").then((m) => ({
      default: m.PaginaRota,
    })),
  ),
  "/franquia-pdv-importar-vendas": lazy(() =>
    import("@/paginas/pg_franquia/franquia-pdv-importar-vendas").then((m) => ({
      default: m.ImportarVendasPage,
    })),
  ),
  "/franquia-pdv-carregar_arquivo-diario": lazy(() =>
    import("@/paginas/pg_franquia/franquia-pdv-carregar_arquivo-diario").then((m) => ({
      default: m.CarregarArquivoDiarioPage,
    })),
  ),
  "/franquia-vendas-main-excluir": lazy(() =>
    import("@/paginas/pg_franquia/franquia-vendas-main-excluir").then((m) => ({
      default: m.ExcluirVendasPage,
    })),
  ),
  "/franquia-vendas-main-dias": lazy(() =>
    import("@/paginas/pg_franquia/franquia-vendas-main-dias").then((m) => ({
      default: m.Vendas30DiasPage,
    })),
  ),
  "/estoques": lazy(() =>
    import("@/paginas/pg_franquia/franquia-estoques-main").then((m) => ({
      default: m.EstoquesPage,
    })),
  ),
  "/franquia-estoques-main-entrada": lazy(() =>
    import("@/paginas/pg_franquia/franquia-estoques-main-entrada").then((m) => ({
      default: m.EntradaEstoquePage,
    })),
  ),
  "/franquia-estoque-reposicao": lazy(() =>
    import("@/paginas/pg_franquia/franquia-estoque-reposicao").then((m) => ({
      default: m.ReposicaoPage,
    })),
  ),
  "/franquia-estoque-reposicao-historico": lazy(() =>
    import("@/paginas/pg_franquia/franquia-estoque-reposicao-historico").then((m) => ({
      default: m.ReposicaoPesquisaPage,
    })),
  ),
  "/franquia-ajustes-estoques-main": lazy(() =>
    import("@/paginas/pg_franquia/franquia-ajustes-estoques-main").then((m) => ({
      default: m.AjustesEstoquesMainPage,
    })),
  ),
  "/franquia-estoques-main-transferencia": lazy(() =>
    import("@/paginas/pg_franquia/franquia-estoques-main-transferencia").then((m) => ({
      default: m.TransferenciaEstoquePage,
    })),
  ),
  "/franquia-estoques-main-perda": lazy(() =>
    import("@/paginas/pg_franquia/franquia-estoques-main-perda").then((m) => ({
      default: m.PerdaEstoquePage,
    })),
  ),
  "/franquia-estoques-main-movimentacoes": lazy(() =>
    import("@/paginas/pg_franquia/franquia-estoques-main-movimentacoes").then((m) => ({
      default: m.MovimentacoesEstoquePage,
    })),
  ),
  "/franquia-estoques-main-baixa-venda": lazy(() =>
    import("@/paginas/pg_franquia/franquia-estoques-main-baixa-venda").then((m) => ({
      default: m.BaixaVendaEstoquePage,
    })),
  ),
  "/franquia-estoque-relatorios-movimentacao": lazy(() =>
    import("@/paginas/pg_franquia/franquia-estoque-relatorios-movimentacao").then((m) => ({
      default: m.RelatorioMovimentacaoPage,
    })),
  ),
  "/franquia-estoques-main-compras-recebidas": lazy(() =>
    import("@/paginas/pg_franquia/franquia-estoques-main-compras-recebidas").then((m) => ({
      default: m.ComprasRecebidasEstoquePage,
    })),
  ),
  "/insumos": lazy(() =>
    import("@/paginas/pg_insumos/insumos-main").then((m) => ({ default: m.PaginaRota })),
  ),
  "/empresa": lazy(() =>
    import("@/paginas/pg_empresa/empresa-main").then((m) => ({ default: m.EmpresaPage })),
  ),
  "/empresa-horarios": lazy(() =>
    import("@/paginas/pg_empresa/empresa-horarios").then((m) => ({ default: m.PaginaRota })),
  ),
  "/sistema": lazy(() =>
    import("@/paginas/pg_sistema/sistema-main").then((m) => ({ default: m.SistemaPage })),
  ),
  "/agenda": lazy(() =>
    import("@/paginas/pg_agenda/agenda-main").then((m) => ({ default: m.AgendaMainPage })),
  ),
  "/assistente-visualizar-mensagens": lazy(() =>
    import("@/paginas/pg_assistente/assistente-visualizar-mensagens").then((m) => ({
      default: m.AssistenteVisualizarMensagensPage,
    })),
  ),
  "/acoes-visualizar": lazy(() =>
    import("@/paginas/pg_acoes/acoes-visualizar").then((m) => ({
      default: m.AcoesVisualizarPage,
    })),
  ),
  "/acoes-preview": lazy(() =>
    import("@/paginas/pg_acoes/acoes-preview").then((m) => ({ default: m.AcoesPreviewPage })),
  ),
  "/acoes-configurar": lazy(() =>
    import("@/paginas/pg_acoes/acoes-configurar").then((m) => ({
      default: m.AcoesConfigurarPage,
    })),
  ),
  "/acoes-crontab": lazy(() =>
    import("@/paginas/pg_acoes/acoes-crontab").then((m) => ({ default: m.AcoesCrontabPage })),
  ),
  "/acoes-main": lazy(() =>
    import("@/paginas/pg_acoes/acoes-main").then((m) => ({ default: m.AcoesMainPage })),
  ),
  "/regressivas-main": lazy(() =>
    import("@/paginas/pg_regressivas/regressivas-main").then((m) => ({
      default: m.RegressivasMainPage,
    })),
  ),
  "/regressivas-config": lazy(() =>
    import("@/paginas/pg_regressivas/regressivas-config").then((m) => ({
      default: m.RegressivasConfigPage,
    })),
  ),
  "/regressivas-editor": lazy(() =>
    import("@/paginas/pg_regressivas/regressivas-editor").then((m) => ({
      default: m.RegressivasEditorPage,
    })),
  ),
  "/regressivas-criar": lazy(() =>
    import("@/paginas/pg_regressivas/regressivas-criar").then((m) => ({
      default: m.RegressivasCriarPage,
    })),
  ),
  "/regressivas-panel": lazy(() =>
    import("@/paginas/pg_regressivas/regressivas-panel").then((m) => ({
      default: m.RegressivasPanelPage,
    })),
  ),
  "/agenda-eventos": lazy(() =>
    import("@/paginas/pg_agenda/agenda-eventos").then((m) => ({ default: m.AgendaEventosPage })),
  ),
  "/agenda-calendario": lazy(() =>
    import("@/paginas/pg_agenda/agenda-calendario").then((m) => ({
      default: m.AgendaCalendarioPage,
    })),
  ),
  "/agenda-configuracao": lazy(() =>
    import("@/paginas/pg_agenda/agenda-configuracao").then((m) => ({
      default: m.AgendaConfiguracaoPage,
    })),
  ),
  "/agenda-ver-calendarios": lazy(() =>
    import("@/paginas/pg_agenda/agenda-ver-calendarios").then((m) => ({
      default: m.AgendaVerCalendariosPage,
    })),
  ),
  "/agenda-ver-registros-google": lazy(() =>
    import("@/paginas/pg_agenda/agenda-ver-registros-google").then((m) => ({
      default: m.AgendaVerRegistrosGooglePage,
    })),
  ),
  "/agenda-criar": lazy(() =>
    import("@/paginas/pg_agenda/agenda-criar").then((m) => ({ default: m.AgendaCriarPage })),
  ),
  "/agenda-editar": lazy(() =>
    import("@/paginas/pg_agenda/agenda-editar").then((m) => ({ default: m.AgendaEditarPage })),
  ),
  "/agenda-editar-tarefa": lazy(() =>
    import("@/paginas/pg_agenda/agenda-editar-tarefa").then((m) => ({
      default: m.AgendaEditarTarefaPage,
    })),
  ),
  "/sistema-parametros": lazy(() =>
    import("@/paginas/pg_sistema/sistema-parametros").then((m) => ({ default: m.ParametrosPage })),
  ),
  "/sistema-feriados": lazy(() =>
    import("@/paginas/pg_feriados/feriados-main").then((m) => ({ default: m.FeriadosPage })),
  ),
  "/sistema-feriados-editar": lazy(() =>
    import("@/paginas/pg_feriados/feriados-editar").then((m) => ({
      default: m.FeriadosEditarPage,
    })),
  ),
  "/usuarios-main": lazy(() =>
    import("@/paginas/pg_usuarios/usuarios-main").then((m) => ({ default: m.UsuariosPage })),
  ),
  "/usuarios-editar": lazy(() =>
    import("@/paginas/pg_usuarios/usuarios-editar").then((m) => ({ default: m.UsuariosEditarPage })),
  ),
  "/usuarios-filiais": lazy(() =>
    import("@/paginas/pg_usuarios/usuarios-filiais").then((m) => ({
      default: m.UsuariosFiliaisPage,
    })),
  ),
  "/empresa-estoques-grupos": lazy(() =>
    import("@/paginas/pg_empresa/empresa-estoques-grupos").then((m) => ({
      default: m.EstoquesGruposPage,
    })),
  ),
  "/usuarios-auditoria": lazy(() =>
    import("@/paginas/pg_usuarios/usuarios-auditoria").then((m) => ({ default: m.AuditoriaPage })),
  ),
  "/viewer-texto": lazy(() =>
    import("@/paginas/pg_viewer/viewer-texto").then((m) => ({ default: m.ViewerTextoPage })),
  ),
  "/viewer-device": lazy(() =>
    import("@/paginas/pg_viewer/viewer-device").then((m) => ({ default: m.ViewerDevicePage })),
  ),
  "/database-main": lazy(() =>
    import("@/paginas/pg_database/database-main").then((m) => ({
      default: m.BancoDeDadosPage,
    })),
  ),
  "/msg-mensagens-leituras": lazy(() =>
    import("@/paginas/pg_msg/msg-mensagens-leituras").then((m) => ({
      default: m.MsgMensagensLeiturasPage,
    })),
  ),
  "/sistema-anexos": lazy(() =>
    import("@/paginas/pg_sistema/sistema-anexos").then((m) => ({ default: m.AnexosPage })),
  ),
  "/sistema-arquivos": lazy(() =>
    import("@/paginas/pg_arquivos/arquivos-main").then((m) => ({ default: m.ArquivosPage })),
  ),
  "/sistema-main-verificar": lazy(() =>
    import("@/paginas/pg_sistema/sistema-main-verificar").then((m) => ({
      default: m.VerificarInconsistenciasSistemaPage,
    })),
  ),
  "/sistema-testar-sistema": lazy(() =>
    import("@/paginas/pg_sistema/sistema-testar-sistema").then((m) => ({
      default: m.TestesSistemaPage,
    })),
  ),
  "/dev-main": lazy(() =>
    import("@/paginas/pg_dev/dev-main").then((m) => ({ default: m.DevPage })),
  ),
  "/dev-pacotes-http-recebidos": lazy(() =>
    import("@/paginas/pg_dev/dev-pacotes-http-recebidos").then((m) => ({
      default: m.PacotesHTTPPage,
    })),
  ),
  "/database-visualizar-dados": lazy(() =>
    import("@/paginas/pg_database/database-visualizar-dados").then((m) => ({
      default: m.EstruturaTabelasPage,
    })),
  ),
  "/database-visualizar-estruturas": lazy(() =>
    import("@/paginas/pg_database/database-visualizar-estruturas").then((m) => ({
      default: m.EstruturaDadosPage,
    })),
  ),
  "/database-sql-tabelas-monitoradas": lazy(() =>
    import("@/paginas/pg_database/database-sql-tabelas-monitoradas").then((m) => ({
      default: m.TabelasMonitoradasPage,
    })),
  ),
  "/database-sql-historico": lazy(() =>
    import("@/paginas/pg_database/database-sql-historico").then((m) => ({
      default: m.SqlHistoricoPage,
    })),
  ),
  "/dev-testes": lazy(() =>
    import("@/paginas/pg_dev/dev-testes").then((m) => ({ default: m.TestesDevPage })),
  ),
  "/database-sql-exportar-tabelas": lazy(() =>
    import("@/paginas/pg_database/database-sql-exportar-tabelas").then((m) => ({
      default: m.ExportarTabelasSqlPage,
    })),
  ),
  "/dev-contatos": lazy(() =>
    import("@/paginas/pg_dev/dev-contatos").then((m) => ({ default: m.DevContatosPage })),
  ),
  "/dev-mensagens": lazy(() =>
    import("@/paginas/pg_dev/dev-mensagens").then((m) => ({ default: m.DevMensagensPage })),
  ),
  "/analises": lazy(() =>
    import("@/paginas/pg_franquia/franquia-analises-main").then((m) => ({
      default: m.AnalisesPage,
    })),
  ),
  "/franquia-analises-vendas": lazy(() =>
    import("@/paginas/pg_franquia/franquia-analises-vendas").then((m) => ({
      default: m.AnaliseVendas,
    })),
  ),
  "/franquia-analise-comparacao-periodos": lazy(() =>
    import("@/paginas/pg_franquia/franquia-analise-comparacao-periodos").then((m) => ({
      default: m.PaginaRota,
    })),
  ),
  "/franquia-analises-simulacao": lazy(() =>
    import("@/paginas/pg_franquia/franquia-analises-simulacao").then((m) => ({
      default: m.SimulacaoPage,
    })),
  ),
  "/franquia-analises-tendencias": lazy(() =>
    import("@/paginas/pg_franquia/franquia-analises-tendencias").then((m) => ({
      default: m.Tendencias,
    })),
  ),
  "/franquia-analises-main-verificar": lazy(() =>
    import("@/paginas/pg_franquia/franquia-analises-main-verificar").then((m) => ({
      default: m.VerificarInconsistenciasPage,
    })),
  ),
  "/franquia-analises-dados-vendas": lazy(() =>
    import("@/paginas/pg_franquia/franquia-analises-dados-vendas").then((m) => ({
      default: m.DadosVendasPage,
    })),
  ),
  "/painel": lazy(() =>
    import("@/paginas/pg_franquia/franquia-painel").then((m) => ({ default: m.PainelPage })),
  ),
  "/produtos": lazy(() =>
    import("@/paginas/pg_franquia/franquia-produtos").then((m) => ({ default: m.ProdutosPage })),
  ),
  "/produtos-editor": lazy(() =>
    import("@/paginas/pg_produtos/produtos-editor").then((m) => ({ default: m.PaginaRota })),
  ),
  "/preferencias": lazy(() =>
    import("@/paginas/pg_preferencias/preferencias-main").then((m) => ({ default: m.ConfigPage })),
  ),
  "/assistente-painel": lazy(() =>
    import("@/paginas/pg_assistente/assistente-painel").then((m) => ({
      default: m.AssistentePainelPage,
    })),
  ),
  "/franquia-analises-avisos": lazy(() =>
    import("@/paginas/pg_franquia/franquia-analises-avisos").then((m) => ({
      default: m.PaginaRota,
    })),
  ),
  "/msg-mensagens": lazy(() =>
    import("@/paginas/pg_msg/msg-mensagens").then((m) => ({ default: m.MsgMensagensPage })),
  ),
  "/msg-mural": lazy(() =>
    import("@/paginas/pg_msg/msg-mural").then((m) => ({ default: m.MsgMuralPage })),
  ),
  "/usuarios-perfil": lazy(() =>
    import("@/paginas/pg_usuarios/usuarios-perfil").then((m) => ({ default: m.PerfilPage })),
  ),
  "/camera-capturar-codigo": lazy(() =>
    import("@/paginas/pg_camera/camera-capturar-codigo").then((m) => ({
      default: m.CapturarCodigoPage,
    })),
  ),
  "/rabelo-testes": lazy(() =>
    import("@/paginas/pg_rabelo/rabelo-testes").then((m) => ({ default: m.RabeloTestesPage })),
  ),
  "/empresa-filiais": lazy(() =>
    import("@/paginas/pg_empresa/empresa-filiais").then((m) => ({ default: m.PaginaRota })),
  ),
  "/empresa-funcionarios": lazy(() =>
    import("@/paginas/pg_empresa/empresa-funcionarios").then((m) => ({
      default: m.FuncionariosPage,
    })),
  ),
  "/assistente-virtual": lazy(() =>
    import("@/paginas/pg_assistente/assistente-virtual").then((m) => ({
      default: m.GerenteVirtualPage,
    })),
  ),
  "/empresa-estoques-depositos": lazy(() =>
    import("@/paginas/pg_empresa/empresa-estoques-depositos").then((m) => ({
      default: m.EstoquesDepositosPage,
    })),
  ),
  "/empresa-compras": lazy(() =>
    import("@/paginas/pg_empresa/empresa-compras").then((m) => ({ default: m.ComprasPage })),
  ),
  "/empresa-fornecedores-cadastro": lazy(() =>
    import("@/paginas/pg_empresa/empresa-fornecedores-cadastro").then((m) => ({
      default: m.FornecedoresCadastroPage,
    })),
  ),
  "/empresa-notas-fiscais-entrada": lazy(() =>
    import("@/paginas/pg_empresa/empresa-notas-fiscais-entrada").then((m) => ({
      default: m.NotasFiscaisEntradaPage,
    })),
  ),
  "/empresa-contas-a-pagar-main": lazy(() =>
    import("@/paginas/pg_empresa/empresa-contas-a-pagar-main").then((m) => ({
      default: m.ContasPage,
    })),
  ),
  "/empresa-contas-a-pagar-main-fornecedores": lazy(() =>
    import("@/paginas/pg_empresa/empresa-contas-a-pagar-main-fornecedores").then((m) => ({
      default: m.FornecedoresContasPage,
    })),
  ),
  "/empresa-contas-a-pagar-main-franquia": lazy(() =>
    import("@/paginas/pg_empresa/empresa-contas-a-pagar-main-franquia").then((m) => ({
      default: m.FranquiaContasPage,
    })),
  ),
  "/empresa-contas-a-pagar-main-contas-consumo": lazy(() =>
    import("@/paginas/pg_empresa/empresa-contas-a-pagar-main-contas-consumo").then((m) => ({
      default: m.ContasConsumoPage,
    })),
  ),
  "/empresa-contas-a-pagar-main-folha-pagamento": lazy(() =>
    import("@/paginas/pg_empresa/empresa-contas-a-pagar-main-folha-pagamento").then((m) => ({
      default: m.FolhaPagamentoPage,
    })),
  ),
  "/empresas-contas-a-pagar-main-alugueis": lazy(() =>
    import("@/paginas/pg_empresa/empresa-contas-a-pagar-main-alugueis").then((m) => ({
      default: m.AlugueisPage,
    })),
  ),
  "/empresa-contas-a-pagar-main-despesas-extras": lazy(() =>
    import("@/paginas/pg_empresa/empresa-contas-a-pagar-main-despesas-extras").then((m) => ({
      default: m.DespesasExtrasPage,
    })),
  ),
  "/empresa-metadados": lazy(() =>
    import("@/paginas/pg_empresa/empresa-metadados").then((m) => ({ default: m.DadosPage })),
  ),
  "/empresa-contatos": lazy(() =>
    import("@/paginas/pg_empresa/empresa-contatos").then((m) => ({
      default: m.AgendaContatosPage,
    })),
  ),
  "/rabelo-main": lazy(() =>
    import("@/paginas/pg_rabelo/rabelo-main").then((m) => ({ default: m.RabeloMainPage })),
  ),
  "/rabelo-posicoes": lazy(() =>
    import("@/paginas/pg_rabelo/rabelo-posicoes").then((m) => ({ default: m.RabeloPosicoesPage })),
  ),
  "/rabelo-referencias": lazy(() =>
    import("@/paginas/pg_rabelo/rabelo-referencias").then((m) => ({
      default: m.RabeloReferenciasPage,
    })),
  ),
  "/atj-main": lazy(() =>
    import("@/paginas/pg_atj/atj-main").then((m) => ({ default: m.AtjMainPage })),
  ),
  "/atj-consultar": lazy(() =>
    import("@/paginas/pg_atj/atj-consultar").then((m) => ({ default: m.AtjConsultarPage })),
  ),
  "/atj-ingest": lazy(() =>
    import("@/paginas/pg_atj/atj-ingest").then((m) => ({ default: m.AtjIngestPage })),
  ),
  "/atj-digitalizacao": lazy(() =>
    import("@/paginas/pg_atj/atj-digitalizacao").then((m) => ({ default: m.AtjDigitalizacaoPage })),
  ),
  "/atj-manutencao": lazy(() =>
    import("@/paginas/pg_atj/atj-manutencao").then((m) => ({ default: m.AtjManutencaoPage })),
  ),
  "/compras-main": lazy(() =>
    import("@/paginas/pg_franquia/franquia-compras-main").then((m) => ({
      default: m.ComprasMainPage,
    })),
  ),
  "/compras-listas": lazy(() =>
    import("@/paginas/pg_franquia/franquia-compras-listas").then((m) => ({
      default: m.ListaComprasPage,
    })),
  ),
  "/compras-recebidas": lazy(() =>
    import("@/paginas/pg_franquia/franquia-compras-recebidas").then((m) => ({
      default: m.PaginaRota,
    })),
  ),
};

const ARQUIVOS_PAGINAS_JANELA: Record<string, string> = {
  "/inicio": "inicio.tsx",
  "/franquia-main": "franquia-main.tsx",
  "/franquia-pdv-main": "franquia-pdv-main.tsx",
  "/franquia-suprimentos-main": "franquia-suprimentos-main.tsx",
  "/franquia-suprimentos-a-caminho": "franquia-suprimentos-a-caminho.tsx",
  "/franquia-pdv-matriz": "franquia-pdv-matriz.tsx",
  "/vendas": "franquia-vendas-main.tsx",
  "/franquia-vendas-manuais": "franquia-vendas-manuais.tsx",
  "/franquia-vendas-registrar-venda": "franquia-vendas-registrar-venda.tsx",
  "/franquia-vendas-manuais-resumo": "franquia-vendas-manuais-resumo.tsx",
  "/franquia-vendas-vendas-dia": "franquia-vendas-vendas-dia.tsx",
  "/insumos-consumo": "insumos-consumo.tsx",
  "/franquia-vendas-registro-manual": "franquia-vendas-registro-manual.tsx",
  "/franquia-pdv-importar-vendas": "franquia-pdv-importar-vendas.tsx",
  "/franquia-pdv-carregar_arquivo-diario": "franquia-pdv-carregar_arquivo-diario.tsx",
  "/franquia-vendas-main-excluir": "franquia-vendas-main-excluir.tsx",
  "/franquia-vendas-main-dias": "franquia-vendas-main-dias.tsx",
  "/estoques": "franquia-estoques-main.tsx",
  "/franquia-estoques-main-entrada": "franquia-estoques-main-entrada.tsx",
  "/franquia-estoque-reposicao": "franquia-estoque-reposicao.tsx",
  "/franquia-estoque-reposicao-historico": "franquia-estoque-reposicao-historico.tsx",
  "/franquia-ajustes-estoques-main": "franquia-ajustes-estoques-main.tsx",
  "/franquia-estoques-main-transferencia": "franquia-estoques-main-transferencia.tsx",
  "/franquia-estoques-main-perda": "franquia-estoques-main-perda.tsx",
  "/franquia-estoques-main-movimentacoes": "franquia-estoques-main-movimentacoes.tsx",
  "/franquia-estoques-main-baixa-venda": "franquia-estoques-main-baixa-venda.tsx",
  "/franquia-estoque-relatorios-movimentacao": "franquia-estoque-relatorios-movimentacao.tsx",
  "/franquia-estoques-main-compras-recebidas": "franquia-estoques-main-compras-recebidas.tsx",
  "/insumos": "insumos-main.tsx",
  "/analises": "franquia-analises-main.tsx",
  "/franquia-analises-vendas": "franquia-analises-vendas.tsx",
  "/franquia-analise-comparacao-periodos": "franquia-analise-comparacao-periodos.tsx",
  "/franquia-analises-simulacao": "franquia-analises-simulacao.tsx",
  "/franquia-analises-tendencias": "franquia-analises-tendencias.tsx",
  "/franquia-analises-main-verificar": "franquia-analises-main-verificar.tsx",
  "/franquia-analises-dados-vendas": "franquia-analises-dados-vendas.tsx",
  "/empresa": "pg_empresa/empresa-main.tsx",
  "/sistema": "pg_sistema/sistema-main.tsx",
  "/sistema-parametros": "pg_sistema/sistema-parametros.tsx",
  "/agenda-editar-tarefa": "pg_agenda/agenda-editar-tarefa.tsx",
  "/agenda-configuracao": "pg_agenda/agenda-configuracao.tsx",
  "/agenda-ver-calendarios": "pg_agenda/agenda-ver-calendarios.tsx",
  "/agenda-ver-registros-google": "pg_agenda/agenda-ver-registros-google.tsx",
  "/sistema-feriados": "pg_feriados/feriados-main.tsx",
  "/sistema-feriados-editar": "pg_feriados/feriados-editar.tsx",
  "/usuarios-main": "pg_usuarios/usuarios-main.tsx",
  "/usuarios-editar": "pg_usuarios/usuarios-editar.tsx",
  "/usuarios-filiais": "pg_usuarios/usuarios-filiais.tsx",
  "/empresa-estoques-grupos": "pg_empresa/empresa-estoques-grupos.tsx",
  "/usuarios-auditoria": "pg_usuarios/usuarios-auditoria.tsx",
  "/viewer-texto": "pg_viewer/viewer-texto.tsx",
  "/viewer-device": "pg_viewer/viewer-device.tsx",
  "/database-main": "pg_database/database-main.tsx",
  "/msg-mensagens-leituras": "msg-mensagens-leituras.tsx",
  "/sistema-anexos": "pg_sistema/sistema-anexos.tsx",
  "/sistema-arquivos": "pg_arquivos/arquivos-main.tsx",
  "/sistema-main-verificar": "pg_sistema/sistema-main-verificar.tsx",
  "/sistema-testar-sistema": "pg_sistema/sistema-testar-sistema.tsx",
  "/dev-main": "pg_dev/dev-main.tsx",
  "/dev-pacotes-http-recebidos": "pg_dev/dev-pacotes-http-recebidos.tsx",
  "/database-visualizar-dados": "pg_database/database-visualizar-dados.tsx",
  "/database-visualizar-estruturas": "pg_database/database-visualizar-estruturas.tsx",
  "/database-sql-tabelas-monitoradas": "pg_database/database-sql-tabelas-monitoradas.tsx",
  "/database-sql-historico": "pg_database/database-sql-historico.tsx",
  "/dev-testes": "pg_dev/dev-testes.tsx",
  "/database-sql-exportar-tabelas": "pg_database/database-sql-exportar-tabelas.tsx",
  "/dev-contatos": "pg_dev/dev-contatos.tsx",
  "/dev-mensagens": "pg_dev/dev-mensagens.tsx",
  "/painel": "franquia-painel.tsx",
  "/produtos": "franquia-produtos.tsx",
  "/produtos-editor": "produtos-editor.tsx",
  "/preferencias": "pg_preferencias/preferencias-main.tsx",
  "/assistente-painel": "pg_assistente/assistente-painel.tsx",
  "/regressivas-main": "pg_regressivas/regressivas-main.tsx",
  "/regressivas-config": "pg_regressivas/regressivas-config.tsx",
  "/regressivas-editor": "pg_regressivas/regressivas-editor.tsx",
  "/regressivas-criar": "pg_regressivas/regressivas-criar.tsx",
  "/regressivas-panel": "pg_regressivas/regressivas-panel.tsx",
  "/acoes-crontab": "pg_acoes/acoes-crontab.tsx",
  "/franquia-analises-avisos": "franquia-analises-avisos.tsx",
  "/msg-mensagens": "msg-mensagens.tsx",
  "/msg-mural": "msg-mural.tsx",
  "/usuarios-perfil": "pg_usuarios/usuarios-perfil.tsx",
  "/camera-capturar-codigo": "pg_camera/camera-capturar-codigo.tsx",
  "/empresa-horarios": "pg_empresa/empresa-horarios.tsx",
  "/empresa-filiais": "pg_empresa/empresa-filiais.tsx",
  "/empresa-funcionarios": "pg_empresa/empresa-funcionarios.tsx",
  "/assistente-virtual": "pg_assistente/assistente-virtual.tsx",
  "/empresa-estoques-depositos": "pg_empresa/empresa-estoques-depositos.tsx",
  "/empresa-compras": "pg_empresa/empresa-compras.tsx",
  "/empresa-fornecedores-cadastro": "pg_empresa/empresa-fornecedores-cadastro.tsx",
  "/empresa-notas-fiscais-entrada": "pg_empresa/empresa-notas-fiscais-entrada.tsx",
  "/empresa-contas-a-pagar-main": "pg_empresa/empresa-contas-a-pagar-main.tsx",
  "/empresa-contas-a-pagar-main-fornecedores":
    "pg_empresa/empresa-contas-a-pagar-main-fornecedores.tsx",
  "/empresa-contas-a-pagar-main-franquia": "pg_empresa/empresa-contas-a-pagar-main-franquia.tsx",
  "/empresa-contas-a-pagar-main-contas-consumo":
    "pg_empresa/empresa-contas-a-pagar-main-contas-consumo.tsx",
  "/empresa-contas-a-pagar-main-folha-pagamento":
    "pg_empresa/empresa-contas-a-pagar-main-folha-pagamento.tsx",
  "/empresa-contas-a-pagar-main-alugueis": "pg_empresa/empresa-contas-a-pagar-main-alugueis.tsx",
  "/empresa-contas-a-pagar-main-despesas-extras":
    "pg_empresa/empresa-contas-a-pagar-main-despesas-extras.tsx",
  "/empresa-metadados": "pg_empresa/empresa-metadados.tsx",
  "/empresa-contatos": "pg_empresa/empresa-contatos.tsx",
  "/rabelo-testes": "pg_rabelo/rabelo-testes.tsx",
  "/rabelo-main": "pg_rabelo/rabelo-main.tsx",
  "/rabelo-posicoes": "pg_rabelo/rabelo-posicoes.tsx",
  "/rabelo-referencias": "pg_rabelo/rabelo-referencias.tsx",
  "/atj-main": "pg_atj/atj-main.tsx",
  "/atj-consultar": "pg_atj/atj-consultar.tsx",
  "/atj-ingest": "pg_atj/atj-ingest.tsx",
  "/atj-digitalizacao": "pg_atj/atj-digitalizacao.tsx",
  "/atj-manutencao": "pg_atj/atj-manutencao.tsx",
  "/compras-main": "franquia-compras-main.tsx",
  "/compras-listas": "franquia-compras-listas.tsx",
  "/compras-recebidas": "franquia-compras-recebidas.tsx",
};

/** Espera enquanto o código da aba é baixado sob demanda. */
function CarregandoJanela() {
  return (
    <div className="flex flex-1 items-center justify-center py-16">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <span className="sr-only">Carregando Janela...</span>
    </div>
  );
}

// Portão de prontidão: só monta o layout (e, portanto, qualquer tela filha e
// suas consultas ao banco) depois que autenticação, banco e parâmetros estão
// confirmados. Em falha definitiva, exibe indisponibilidade fatal.
function AppLayout() {
  return (
    <GuardaProntidao>
      <AppLayoutInterno />
    </GuardaProntidao>
  );
}

function AppLayoutInterno() {
  const navigate = useNavigate();
  const { nivel } = useNivel();
  const { user } = useAuth();
  // Mantém o modo de visualização de valores sincronizado globalmente.
  useValores();
  // Impede qualquer gravação no banco para quem não vê os valores reais.
  // Direciona todas as leituras/gravações ao banco escolhido (Cloud ou externo).
  instalarPonteBancoExterno();
  instalarGuardaPersistencia();
  // A cada troca de aba, sorteia novos valores simulados.
  const caminho = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    renovarSementeSimulacao();
  }, [caminho]);

  // Toda aba abre no topo; telas com regra própria restauram a rolagem depois.
  useEffect(() => {
    const main = document.querySelector("main");
    if (main) main.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [caminho]);

  // Depois que a tela principal aparece, baixa em segundo plano TODAS as abas
  // de src/paginas (as mais usadas primeiro), registrando no console.
  useEffect(() => {
    precarregarPaginas(["inicio", "vendas", "estoques", "painel"]);
    void carregarAgendaEventosCache();
  }, []);

  const [menuAberto, setMenuAberto] = useState(false);
  // Pilha de janelas-página abertas: o retorno desempilha e revela a que a
  // chamou (ex.: de franquia-main ��� Vendas, voltar retorna a franquia-main).
  const [pilhaPaginas, setPilhaPaginas] = useState<string[]>([]);
  const janelaInicio = pilhaPaginas[pilhaPaginas.length - 1] ?? null;
  // Empilha uma nova janela sobre a atual (usado pelos ladrilhos internos).
  const abrirPagina = useCallback((destino: string) => {
    setPilhaPaginas((p) => (p[p.length - 1] === destino ? p : [...p, destino]));
  }, []);
  // Reinicia a pilha numa única janela (menu/cabeçalho = navegação de topo).
  const trocarPagina = useCallback((destino: string) => setPilhaPaginas([destino]), []);
  // Fecha apenas a janela do topo, revelando a que a chamou. Idempotente por
  // gesto: um clique de "voltar" remove exatamente um nível, mesmo que o fechar
  // seja disparado em duplicidade (ex.: BotaoVoltar chama aoAcionar + fecharJanela).
  const fechandoRef = useRef(false);
  const fecharTopo = useCallback(() => {
    if (fechandoRef.current) return;
    fechandoRef.current = true;
    queueMicrotask(() => {
      fechandoRef.current = false;
    });
    setPilhaPaginas((p) => p.slice(0, -1));
  }, []);
  // Fecha todas as janelas-página (ex.: ao navegar de rota).
  const fecharTudo = useCallback(() => setPilhaPaginas([]), []);
  const { lado } = useMargemLateral();
  const margemEstilo = {
    paddingLeft: `max(20px, ${lado}%)`,
    paddingRight: `max(20px, ${lado}%)`,
  };
  // O cabeçalho usa margens menores para sobrar espaço no celular.
  const margemHeader = {
    paddingLeft: `max(4px, ${lado}%)`,
    paddingRight: `max(4px, ${lado}%)`,
  };
  const { ativos } = useAvisos();
  const naoLidos = useNaoLidos();
  const { nomeApp, nomeCurto } = useMarca();
  const {
    tempo_inatividade,
    altura_botoes_metro,
    largura_botoes_metro,
    margens_titulo,
    margens_botoes_retorno,
  } = useParametros();

  // Altura dos botões metro (parâmetro do aplicativo).
  useEffect(() => {
    const alt = Math.min(180, Math.max(20, Math.round(Number(altura_botoes_metro) || 160)));
    document.documentElement.style.setProperty("--metro-h", `${alt}px`);
  }, [altura_botoes_metro]);

  // Largura mínima dos botões metro (parâmetro do aplicativo).
  useEffect(() => {
    const larg = Math.min(600, Math.max(250, Math.round(Number(largura_botoes_metro) || 250)));
    document.documentElement.style.setProperty("--metro-w", `${larg}px`);
  }, [largura_botoes_metro]);

  // Margens verticais do título das janelas (parâmetro do aplicativo).
  useEffect(() => {
    const m = Math.min(30, Math.max(1, Math.round(Number(margens_titulo) || 5)));
    document.documentElement.style.setProperty("--titulo-my", `${m}px`);
  }, [margens_titulo]);

  // Margens laterais dos botões de retorno (parâmetro do aplicativo).
  useEffect(() => {
    const m = Math.min(50, Math.max(0, Math.round(Number(margens_botoes_retorno ?? 20))));
    document.documentElement.style.setProperty("--retorno-mx", `${m}px`);
  }, [margens_botoes_retorno]);

  // Altura do cabeçalho: usada pelas janelas cheias para ocupar o resto da tela.
  useEffect(() => {
    const el = document.querySelector("header");
    if (!el) return;
    const medir = () =>
      document.documentElement.style.setProperty(
        "--header-h",
        `${Math.round(el.getBoundingClientRect().height)}px`,
      );
    medir();
    const obs = new ResizeObserver(medir);
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (nivel !== 5) return;
    toast.error("Seu acesso foi suspenso. Fale com um administrador.");
    (async () => {
      const banco = await obterClienteDados();
      await (banco as any).auth.signOut({ scope: "local" });
      navigate({ to: "/auth" });
    })();
  }, [nivel, navigate]);

  // Desconexão automática por inatividade (0 = desligada)
  useEffect(() => {
    const minutos = Number(tempo_inatividade ?? 0);
    if (!minutos || minutos <= 0) return;
    let temporizador: ReturnType<typeof setTimeout>;
    const encerrar = async () => {
      toast.error("Sessão encerrada por inatividade.");
      const banco = await obterClienteDados();
      await (banco as any).auth.signOut({ scope: "local" });
      navigate({ to: "/auth" });
    };
    const reiniciar = () => {
      clearTimeout(temporizador);
      temporizador = setTimeout(() => void encerrar(), minutos * 60 * 1000);
    };
    const eventos = ["mousedown", "keydown", "wheel", "touchstart", "scroll", "visibilitychange"];
    eventos.forEach((ev) => document.addEventListener(ev, reiniciar, true));
    reiniciar();
    return () => {
      clearTimeout(temporizador);
      eventos.forEach((ev) => document.removeEventListener(ev, reiniciar, true));
    };
  }, [tempo_inatividade, navigate]);

  useEffect(() => {
    if (!janelaInicio) return;
    const aoTeclar = (e: KeyboardEvent) => {
      // Só fecha a janela de página quando nenhuma outra janela estiver por cima.
      if (e.key !== "Escape") return;
      // Só fecha quando não há janela cheia aberta por cima (a contagem é
      // confiável mesmo que o DOM já tenha sido atualizado neste mesmo ESC).
      if (janelasAbertas() > 0) return;
      if (document.querySelector("[data-radix-dialog-content], [role='dialog']")) return;
      fecharTopo();
    };
    document.addEventListener("keydown", aoTeclar);
    return () => document.removeEventListener("keydown", aoTeclar);
  }, [janelaInicio, fecharTopo]);

  useEffect(() => {
    if (consumirRetornoFisicoDeJanela()) return;
    fecharTudo();
  }, [caminho, fecharTudo]);

  // Ladrilhos internos (ex.: /inicio) pedem para abrir a aba em janela sem bordas.
  useEffect(
    () =>
      ouvirJanelaPagina((destino) => {
        if (PAGINAS_JANELA[destino]) abrirPagina(destino);
        else navigate({ to: destino });
      }),
    [navigate, abrirPagina],
  );

  useEffect(() => {
    if (!menuAberto) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-menu-sanduiche]")) setMenuAberto(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuAberto]);

  const visiveis = links.filter((l) => nivel <= l.nivelMaximo);

  const { data: perfil } = useQuery({
    queryKey: ["meu-perfil", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const banco = await obterClienteDados();
      const { data, error } = await (banco as any)
        .from("profiles")
        .select("apelido, nome, email, foto_url")
        .eq("id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return data as any;
    },
  });

  const caminhoFoto = (perfil?.foto_url ?? "").trim();

  const { data: fotoUrl } = useQuery({
    queryKey: ["header-foto", caminhoFoto],
    enabled: !!caminhoFoto,
    queryFn: async () => {
      const banco = await obterClienteDados();
      const { data, error } = await (banco as any).storage
        .from("avatares")
        .createSignedUrl(caminhoFoto, 60 * 60);
      if (error) throw error;
      return data.signedUrl;
    },
  });

  const nome = nomeExib(perfil, user?.email ?? "");

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background">
      <header className="z-[1000] shrink-0 border-b border-border bg-card/85 backdrop-blur">
        <div
          style={margemHeader}
          className="mx-auto flex w-full max-w-none items-center gap-0.5 px-1 py-3 sm:gap-3 sm:px-[4px]"
        >
          <Link
            to="/inicio"
            className="flex min-w-0 items-center gap-0.5 text-primary sm:gap-1"
            onClick={(evento) => {
              evento.preventDefault();
              fecharTudo();
              navigate({ to: "/inicio" });
            }}
          >
            <img
              src={logoUrl}
              alt={nomeApp}
              className="h-[50px] w-[50px] shrink-0 object-contain sm:h-10 sm:w-10"
            />
            <span className="truncate font-display text-base font-semibold min-[430px]:hidden">
              {nomeCurto}
            </span>
            <span className="hidden truncate font-display text-base font-semibold min-[430px]:inline">
              {nomeApp}
            </span>
          </Link>

          <div className="flex-1" />
          {ativos.length > 0 && (
            <button
              type="button"
              onClick={() => trocarPagina("/franquia-analises-avisos")}
              title={`${ativos.length} inconsistência(s) detectada(s)`}
              className="relative flex shrink-0 items-center gap-1 rounded-full bg-[#FFFF80] px-2 py-1 text-xs font-semibold text-black"
            >
              <AlertTriangle className="h-4 w-4" />
              {ativos.length}
            </button>
          )}
          <button
            type="button"
            onClick={() => trocarPagina("/msg-mensagens")}
            title={
              naoLidos.mensagens > 0
                ? `${naoLidos.mensagens} mensagem(ns) não lida(s)`
                : "Mensagens"
            }
            className={`relative flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold text-black transition-colors ${
              naoLidos.mensagens > 0 ? "bg-[#FF8000]" : "bg-muted text-muted-foreground"
            }`}
          >
            <MessageSquare className="h-4 w-4" />
            {naoLidos.mensagens > 0 ? naoLidos.mensagens : "0"}
          </button>
          <button
            type="button"
            onClick={() => trocarPagina("/assistente-visualizar-mensagens")}
            title={
              naoLidos.assistente > 0
                ? `${naoLidos.assistente} mensagem(ns) do assistente virtual sem confirmação`
                : "Assistente virtual"
            }
            className={`relative flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold text-black transition-colors ${
              naoLidos.assistente > 0 ? "bg-[#FF4CD5]" : "bg-[#F9E5FF]"
            }`}
          >
            <Bot className="h-4 w-4" />
            {naoLidos.assistente > 0 ? naoLidos.assistente : "0"}
          </button>
          <button
            type="button"
            onClick={() => trocarPagina("/msg-mural")}
            title={
              naoLidos.mural > 0
                ? `${naoLidos.mural} recado(s) do mural sem confirmação de leitura`
                : "Mural"
            }
            className={`relative flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold text-black transition-colors ${
              naoLidos.mural > 0 ? "bg-[#FF8080]" : "bg-muted text-muted-foreground"
            }`}
          >
            <Pin className="h-4 w-4" />
            {naoLidos.mural > 0 ? naoLidos.mural : "0"}
          </button>
          <NotificadorCodigos />
          <button
            type="button"
            onClick={() => trocarPagina("/usuarios-perfil")}
            title="Perfil do usuário"
            className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-muted hover:bg-muted/80"
          >
            {fotoUrl ? (
              <img
                src={fotoUrl}
                alt={`Foto de perfil de ${nome}`}
                className="h-full w-full object-cover"
              />
            ) : (
              <User className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
          <div className="relative shrink-0" data-menu-sanduiche>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMenuAberto((v) => !v)}
              aria-label="Menu"
              aria-expanded={menuAberto}
            >
              <Menu className="h-[26px] w-[26px]" />
            </Button>
            {menuAberto && (
              <div className="absolute right-0 top-full z-[1001] mt-2 w-56 rounded-lg border border-border bg-card p-2 shadow-lg">
                <nav className="flex flex-col gap-1">
                  {visiveis.map((l, idx) => (
                    <div key={l.to} className="contents">
                      <button
                        type="button"
                        onClick={() => {
                          try {
                            try {
                              removeSessionItem("origem-analises");
                            } catch {}
                          } catch {
                            /* ignora */
                          }
                          setMenuAberto(false);
                          if (PAGINAS_JANELA[l.to]) trocarPagina(l.to);
                          else navigate({ to: l.to as any });
                        }}
                        className="rounded-md px-3 py-2 text-left text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      >
                        {l.label}
                      </button>
                      {idx === 0 && <div className="my-1 border-t border-border" />}
                    </div>
                  ))}
                  <div className="my-1 border-t border-border" />
                  {nome && (
                    <button
                      type="button"
                      onClick={() => {
                        setMenuAberto(false);
                        trocarPagina("/usuarios-perfil");
                      }}
                      className="flex items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                      <User className="h-4 w-4" />
                      <span className="max-w-[180px] truncate" title={nome}>
                        {nome}
                      </span>
                    </button>
                  )}
                  <div className="my-1 border-t border-border" />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      const banco = await obterClienteDados();
                      await (banco as any).auth.signOut({ scope: "local" });
                      navigate({ to: "/auth" });
                    }}
                    className="justify-start px-3 py-2"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sair
                  </Button>
                </nav>
              </div>
            )}
          </div>
        </div>
        {/* Menu horizontal secundário com botões retangulares estilo metro. */}
        <nav
          style={margemHeader}
          className="mx-auto hidden w-full max-w-none gap-2 overflow-x-auto px-[4px] pb-2 lg:flex"
        >
          {visiveis.map((l) => {
            const Icon = l.icon;
            const ativo = janelaInicio ? janelaInicio === l.to : caminho === l.to;
            return (
              <button
                key={`ret-${l.to}`}
                type="button"
                onClick={() =>
                  PAGINAS_JANELA[l.to] ? trocarPagina(l.to) : navigate({ to: l.to as any })
                }
                className={`inline-flex h-[30px] flex-row items-center justify-start gap-2 rounded-none px-2 text-xs whitespace-nowrap text-white transition-transform hover:scale-[1.02] focus:outline-none ${
                  ativo ? "ring-2 ring-[#FF0000] ring-inset" : ""
                }`}
                style={{ backgroundColor: l.cor }}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="whitespace-nowrap">{l.label}</span>
              </button>
            );
          })}
        </nav>
      </header>
      {janelaInicio && PAGINAS_JANELA[janelaInicio] ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <div
            style={margemEstilo}
            data-scroll-container
            className="mx-auto flex min-h-0 w-full max-w-none flex-1 flex-col overflow-hidden border-0 bg-background px-[10px]"
            onClickCapture={(e) => {
              if ((e.target as HTMLElement).closest("a")) fecharTudo();
            }}
          >
            {pilhaPaginas.map((pagina) => {
              const Pagina = PAGINAS_JANELA[pagina];
              if (!Pagina) return null;
              const ativa = pagina === janelaInicio;
              return (
                <div
                  key={pagina}
                  className="min-h-0 flex-1 overflow-y-auto"
                  hidden={!ativa}
                  inert={!ativa}
                >
                  <JanelaProvider
                    aoFechar={() => fecharTopo()}
                    arquivo={ARQUIVOS_PAGINAS_JANELA[pagina]}
                    titulo={pagina}
                  >
                    <Suspense fallback={<CarregandoJanela />}>
                      <Pagina />
                    </Suspense>
                  </JanelaProvider>
                  <RodapeCompilacao />
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <main
          style={margemEstilo}
          className="mx-auto flex w-full max-w-none flex-1 flex-col overflow-y-auto px-[10px]"
        >
          <Outlet />
        </main>
      )}
    </div>
  );
}
